import { fetchLiveNow, type LiveNow, type LiveNowEntry } from "./globalSearchApi";

const STORE_KEY = "te-live-now";
const CHANNEL_NAME = "te-live-now";
const SOFT_TTL_MS = 2_500;
const HARD_TTL_MS = 5 * 60_000;
const CLAIM_TTL_MS = 2_000;

interface StoredShape {
  at: number;
  owner: string;
  claimedAt: number;
  entries: LiveNowEntry[];
  liveStudentIds: number[];
  liveUserIds: number[];
  examByStudentId: Record<number, string>;
}

export interface LiveNowSnapshot {
  value: LiveNow;
  at: number;
  stale: boolean;
}

const toStored = (owner: string, value: LiveNow, claimedAt: number): StoredShape => ({
  at: Date.now(),
  owner,
  claimedAt,
  entries: value.entries,
  liveStudentIds: [...value.liveStudentIds],
  liveUserIds: [...value.liveUserIds],
  examByStudentId: value.examByStudentId,
});

const fromStored = (stored: StoredShape): LiveNow => ({
  entries: stored.entries ?? [],
  liveStudentIds: new Set(stored.liveStudentIds ?? []),
  liveUserIds: new Set(stored.liveUserIds ?? []),
  examByStudentId: stored.examByStudentId ?? {},
  scanned: 0,
  total: 0,
});

function readStored(owner: string): StoredShape | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredShape;
    return parsed && parsed.owner === owner ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * BroadcastChannel по спецификации НЕ доставляет сообщение тому, кто его отправил.
 * Поэтому читатели в этой же вкладке (палитра, список заданий, список студентов)
 * узнавали об обновлении только на своём собственном тике — и обновлялись вразнобой.
 * Держим локальный список подписчиков и дёргаем их сразу при записи.
 */
const LOCAL_LISTENERS = new Set<(stored: StoredShape) => void>();

function writeStored(stored: StoredShape): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(stored));
    channel()?.postMessage({ at: stored.at, owner: stored.owner });
  } catch {
    // запись могла не пройти (квота, приватный режим) — подписчиков всё равно уведомим
  }

  for (const listener of LOCAL_LISTENERS) listener(stored);
}

let sharedChannel: BroadcastChannel | null = null;

function channel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return null;
  if (!sharedChannel) sharedChannel = new BroadcastChannel(CHANNEL_NAME);

  return sharedChannel;
}

export function subscribeLiveNow(owner: string, onUpdate: (snapshot: LiveNowSnapshot) => void) {
  const local = (stored: StoredShape) => {
    if (stored.owner !== owner || stored.at === 0) return;

    onUpdate({ value: fromStored(stored), at: stored.at, stale: false });
  };

  LOCAL_LISTENERS.add(local);

  const bus = channel();

  const handler = (event: MessageEvent<{ owner?: string }>) => {
    if (event.data?.owner !== owner) return;

    const stored = readStored(owner);
    if (!stored) return;

    onUpdate({ value: fromStored(stored), at: stored.at, stale: false });
  };

  bus?.addEventListener("message", handler);

  return () => {
    LOCAL_LISTENERS.delete(local);
    bus?.removeEventListener("message", handler);
  };
}

function claim(owner: string, stored: StoredShape | null): boolean {
  const now = Date.now();

  if (stored && now - stored.claimedAt < CLAIM_TTL_MS) return false;

  if (stored) {
    writeStored({ ...stored, claimedAt: now });
    return true;
  }

  writeStored({
    at: 0,
    owner,
    claimedAt: now,
    entries: [],
    liveStudentIds: [],
    liveUserIds: [],
    examByStudentId: {},
  });

  return true;
}

let refreshing: Promise<LiveNow> | null = null;

async function refresh(owner: string): Promise<LiveNow> {
  if (refreshing) return refreshing;

  refreshing = fetchLiveNow()
    .then((value) => {
      writeStored(toStored(owner, value, 0));
      return value;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

export async function getLiveNow(
  owner: string,
  options: { force?: boolean; onBackground?: (snapshot: LiveNowSnapshot) => void } = {}
): Promise<LiveNowSnapshot> {
  const stored = readStored(owner);
  const age = stored && stored.at > 0 ? Date.now() - stored.at : Infinity;

  if (options.force) {
    claim(owner, stored);
    const value = await refresh(owner);
    return { value, at: Date.now(), stale: false };
  }

  if (age < SOFT_TTL_MS && stored) {
    return { value: fromStored(stored), at: stored.at, stale: false };
  }

  if (age < HARD_TTL_MS && stored) {
    if (claim(owner, stored)) {
      void refresh(owner).then((value) => {
        options.onBackground?.({ value, at: Date.now(), stale: false });
      });
    }

    return { value: fromStored(stored), at: stored.at, stale: true };
  }

  claim(owner, stored);
  const value = await refresh(owner);

  return { value, at: Date.now(), stale: false };
}

export function dropLiveNowStore(): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch {
    return;
  }
}
